import React from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Target, Wand2, Gauge } from "lucide-react";

/**
 * Resume Makeover (formerly IMPRESS) panel.
 * Empty state: gentle nudge to add resume or paste JD.
 * Filled state: fit-score banner, action buttons (Open / Boost / Studio),
 * top matching skills, top gaps, and a compact ATS readiness strip when a
 * tailored analysis exists.
 */
export default function ImpressPanel({ hasResume, makeover, fallbackFitScore, fallbackFitSource, studioStatus }) {
  const navigate = useNavigate();
  const directFit = Number(makeover?.fit_score) || 0;
  const fallbackFit = Number(fallbackFitScore) || 0;
  const fit = directFit || fallbackFit;
  const fitColor = fit >= 75 ? "#20C1A7" : fit >= 55 ? "#FFC145" : fit > 0 ? "#FF6A00" : "#94A3B8";
  const verdict = makeover?.verdict || "";

  return (
    <div
      className="rm-card-static p-4 md:p-5"
      style={{
        background: "linear-gradient(135deg, #FFF4E0 0%, #FFE6C7 100%)",
        borderColor: "#0F1B2D",
        boxShadow: "5px 5px 0 0 #0F1B2D",
      }}
      data-testid="impress-panel"
    >
      <div className="min-w-0">

      {!makeover ? (
        <div
          className="p-4"
          style={{ background: "rgba(255,255,255,0.96)", border: "2px solid #0F1B2D" }}
          data-testid="impress-empty"
        >
          {fit > 0 && (
            <div className="flex items-center gap-3 mb-3 pb-3"
                 style={{ borderBottom: "1px solid #E2E8F0" }}
                 data-testid="impress-fit-fallback">
              <div className="font-display leading-none px-2 py-1 flex-shrink-0"
                   style={{
                     background: fitColor, color: "white", border: "2px solid #0F1B2D",
                     fontSize: "1.4rem", minWidth: 52, textAlign: "center",
                   }}
                   data-testid="impress-fit-score">
                {fit}<span style={{ fontSize: "0.5em", opacity: 0.75 }}>/100</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#475569" }}>
                  Fit Score (from {fallbackFitSource || "history"})
                </div>
                <div className="text-xs leading-snug" style={{ color: "#0F1B2D", fontWeight: 500 }}>
                  Drop a fresh Job Description to refresh and tailor.
                </div>
              </div>
            </div>
          )}
          <div className="font-display text-base uppercase mb-1.5">
            {hasResume ? "Got a job description?" : "Add your resume first."}
          </div>
          <p className="text-xs leading-snug mb-3" style={{ color: "#475569" }}>
            {hasResume
              ? "Paste a job description and Resmo will tailor your resume to it — fit score, alignment matrix, and a ready-to-send tailored version."
              : "Once your resume's on file, you can paste any job description to get a tailored version + a fit score in under a minute."}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate(hasResume ? "/feed-job" : "/feed-resume")}
              className="rm-btn rm-btn-primary text-xs"
              data-testid="impress-cta"
            >
              <Wand2 size={12} /> {hasResume ? "Paste a job description" : "Add resume"} →
            </button>
            <button
              onClick={() => navigate("/studio")}
              className="rm-btn text-xs"
              style={{
                background: "linear-gradient(135deg, #F97316 0%, #B91C1C 100%)",
                color: "white",
                border: "2px solid #1E3A5F",
                boxShadow: "2px 2px 0 0 #1E3A5F",
              }}
              data-testid="impress-studio-cta"
            >
              <Wand2 size={12} /> Resume Studio
            </button>
          </div>
        </div>
      ) : (
        <div data-testid="impress-makeover">
          {/* Fit-score banner */}
          <div
            className="p-3 mb-3 flex items-center gap-3"
            style={{
              background: "rgba(30,58,95,0.96)",
              border: "2px solid #1E3A5F",
              boxShadow: "3px 3px 0 0 #FFC145",
            }}
            data-testid="impress-fit-banner"
          >
            <div className="text-center flex-shrink-0">
              <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-0.5"
                   style={{ color: "#FFC145" }}>Fit Score</div>
              <div
                className="font-display leading-none px-2 py-1 inline-block"
                style={{
                  background: fitColor, color: "white", border: "2px solid #FFC145",
                  fontSize: "1.4rem", minWidth: 52, textAlign: "center",
                }}
                data-testid="impress-fit-score"
              >
                {fit > 0 ? fit : "—"}{fit > 0 && <span style={{ fontSize: "0.5em", opacity: 0.75 }}>/100</span>}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#FFC145" }}>
                Alignment with {makeover.company_name || "the role"}
              </div>
              <div className="font-display text-sm uppercase truncate" style={{ color: "white" }}>
                {makeover.job_title || "Tailored resume"}
              </div>
              {verdict && (
                <div className="text-[11px] leading-snug mt-0.5" style={{ color: "#CBD5E1" }}>
                  {verdict}
                </div>
              )}
            </div>
          </div>

          {/* Primary actions — Open · Boost · Resume Studio */}
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="impress-primary-actions">
            <button
              onClick={() => navigate(`/dashboard/${makeover.analysis_id}`)}
              className="rm-btn rm-btn-primary justify-center py-2 text-[11px]"
              data-testid="impress-action-open"
              style={{ background: "white", color: "#0F1B2D", borderColor: "#0F1B2D" }}
            >
              <FileText size={12} />
              Open
            </button>
            <button
              onClick={() => navigate(`/dashboard/${makeover.analysis_id}`)}
              className="rm-btn rm-btn-primary justify-center py-2 text-[11px]"
              data-testid="impress-action-retailor"
              style={{ background: "white", color: "#0F1B2D", borderColor: "#0F1B2D" }}
            >
              <Target size={12} />
              Boost
            </button>
            <button
              onClick={() => navigate(`/studio?analysis_id=${makeover.analysis_id}`)}
              className="rm-btn rm-btn-primary justify-center py-2 text-[11px]"
              data-testid="impress-action-studio"
              style={{
                background: "linear-gradient(135deg, #F97316 0%, #B91C1C 100%)",
                color: "#0F1B2D",
                borderColor: "#0F1B2D",
                boxShadow: "2px 2px 0 0 #0F1B2D",
                fontWeight: 700,
              }}
            >
              <Wand2 size={12} />
              Resume Studio
            </button>
          </div>

          {/* Top 3 Matching Skills */}
          {Array.isArray(makeover.top_skills) && makeover.top_skills.length > 0 && (
            <div className="mb-3" data-testid="impress-top-skills">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1.5"
                   style={{ color: "#0F1B2D", fontWeight: 700 }}>
                ✓ Top 3 Matching Skills
              </div>
              <div className="flex flex-wrap gap-1.5">
                {makeover.top_skills.slice(0, 3).map((s, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 text-[11px] font-semibold"
                    style={{
                      background: "#1E3A5F",
                      color: "#20C1A7",
                      border: "2px solid #1E3A5F",
                      borderRadius: 4,
                    }}
                    data-testid={`impress-skill-${i}`}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Top Gaps */}
          {Array.isArray(makeover.gaps) && makeover.gaps.length > 0 && (
            <div className="mb-3" data-testid="impress-gaps">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1.5"
                   style={{ color: "#991B1B", fontWeight: 700 }}>
                ⚡ Gaps to address
              </div>
              <div className="flex flex-wrap gap-1.5">
                {makeover.gaps.slice(0, 3).map((g, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 text-[11px] font-semibold"
                    style={{
                      background: "#FEF2F2",
                      color: "#991B1B",
                      border: "2px solid #B91C1C",
                      borderRadius: 4,
                    }}
                    data-testid={`impress-gap-${i}`}
                  >
                    {g}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ATS Readiness — surfaces Studio's score + tweak counts inline */}
          {studioStatus && (studioStatus.suggestions_total > 0 || studioStatus.latest_ats_score) && (
            <div
              className="pt-3 mt-1"
              style={{ borderTop: "2px dashed rgba(15,27,45,0.25)" }}
              data-testid="impress-ats"
            >
              <div className="flex items-center gap-2 mb-2">
                <Gauge size={12} style={{ color: "#0F1B2D" }} />
                <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                      style={{ color: "#0F1B2D", fontWeight: 700 }}>
                  ATS Readiness
                </span>
                {studioStatus.latest_ats_score != null && (
                  <span
                    className="font-display text-[11px] px-1.5 py-0.5 rounded ml-1"
                    style={{
                      background: studioStatus.latest_ats_score >= 75 ? "#10B981"
                                : studioStatus.latest_ats_score >= 60 ? "#FFC145"
                                : "#DC2626",
                      color: "white",
                    }}
                    data-testid="impress-ats-score"
                  >
                    {studioStatus.latest_ats_score}/100
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 gap-1.5 mb-2">
                <div className="text-center py-1.5 rounded"
                     style={{ background: "white", border: "2px solid #DC2626" }}
                     data-testid="impress-ats-critical">
                  <div className="font-display text-lg leading-none" style={{ color: "#DC2626" }}>
                    {studioStatus.critical || 0}
                  </div>
                  <div className="font-mono-rm text-[8px] uppercase tracking-widest opacity-80 mt-0.5">
                    Critical
                  </div>
                </div>
                <div className="text-center py-1.5 rounded"
                     style={{ background: "white", border: "2px solid #FFC145" }}
                     data-testid="impress-ats-warn">
                  <div className="font-display text-lg leading-none" style={{ color: "#B45309" }}>
                    {studioStatus.warn || 0}
                  </div>
                  <div className="font-mono-rm text-[8px] uppercase tracking-widest opacity-80 mt-0.5">
                    Should fix
                  </div>
                </div>
                <div className="text-center py-1.5 rounded"
                     style={{ background: "white", border: "2px solid #10B981" }}
                     data-testid="impress-ats-info">
                  <div className="font-display text-lg leading-none" style={{ color: "#10B981" }}>
                    {studioStatus.info || 0}
                  </div>
                  <div className="font-mono-rm text-[8px] uppercase tracking-widest opacity-80 mt-0.5">
                    Nice-to-have
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate(`/studio?analysis_id=${makeover.analysis_id}`)}
                className="w-full rm-btn rm-btn-primary justify-center py-2 text-[11px]"
                data-testid="impress-ats-open-studio"
                style={{
                  background: "linear-gradient(135deg, #F97316 0%, #B91C1C 100%)",
                  color: "#0F1B2D",
                  borderColor: "#0F1B2D",
                  boxShadow: "2px 2px 0 0 #0F1B2D",
                  fontWeight: 700,
                }}
              >
                <Wand2 size={12} />
                Polish in Studio · {studioStatus.suggestions_total} tweaks ready
              </button>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
